import type {
  AnnotationSequenceDirectorySnapshot,
  AnnotationSequenceDirectorySource,
  AnnotationSequenceSummary,
  SequenceDirectoryQuery,
} from "@carbon6/c6-frontend-design/annotation";
import {
  getAnnotationStructure,
  type AnnotationStructureQuery,
  type AnnotationStructureResp,
} from "@/api";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";

const PAGE_SIZE = 50;
const DIRECTORY_ERROR = {
  en: "Could not load the episode directory.",
  ko: "에피소드 목록을 불러오지 못했습니다.",
} as const;

type LoadStructure = (
  datasetUuid: string,
  query: AnnotationStructureQuery,
  options?: { signal?: AbortSignal },
) => Promise<AnnotationStructureResp>;

function sameQuery(left: SequenceDirectoryQuery, right: SequenceDirectoryQuery): boolean {
  const leftAttention = left.attentionSequenceIds;
  const rightAttention = right.attentionSequenceIds;
  return (
    left.streamId === right.streamId &&
    (left.contentScope ?? "all") === (right.contentScope ?? "all") &&
    (leftAttention === rightAttention ||
      (leftAttention !== undefined &&
        rightAttention !== undefined &&
        leftAttention.length === rightAttention.length &&
        leftAttention.every((id, index) => id === rightAttention[index])))
  );
}

export class RegistryAnnotationSequenceDirectorySource implements AnnotationSequenceDirectorySource {
  private readonly listeners = new Set<() => void>();
  private readonly byId = new Map<string, AnnotationSequenceSummary>();
  private readonly ensureRequests = new Map<string, Promise<AnnotationSequenceSummary>>();
  private snapshot: AnnotationSequenceDirectorySnapshot;
  private request: AbortController | null = null;
  private generation = 0;
  private page = 0;

  constructor(
    private readonly options: {
      datasetUuid: string;
      totalSequenceCount: number;
      identities: AnnotationIdentityRegistry;
      loadStructure?: LoadStructure;
      onCameras?: (cameras: readonly string[]) => void;
      getContentSequences?: (
        scope: "captured" | "annotated",
        streamId: string | null,
      ) => readonly AnnotationSequenceSummary[];
    },
  ) {
    this.snapshot = {
      query: { streamId: null, contentScope: "all" },
      items: [],
      totalSequenceCount: options.totalSequenceCount,
      streamFacets: [],
      hasNextPage: false,
      status: "idle",
      isLoadingNextPage: false,
    };
  }

  getSnapshot = (): AnnotationSequenceDirectorySnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  start(): void {
    if (this.snapshot.status !== "idle") return;
    this.resetAndLoad(this.snapshot.query);
  }

  publishTotalSequenceCount(totalSequenceCount: number): void {
    if (
      !Number.isInteger(totalSequenceCount) ||
      totalSequenceCount < 0 ||
      totalSequenceCount === this.snapshot.totalSequenceCount
    ) {
      return;
    }
    this.publish({ ...this.snapshot, totalSequenceCount });
  }

  setQuery(query: SequenceDirectoryQuery): void {
    if (sameQuery(query, this.snapshot.query)) return;
    this.resetAndLoad({
      ...query,
      contentScope: query.contentScope ?? "all",
      attentionSequenceIds:
        query.attentionSequenceIds === undefined ? undefined : [...query.attentionSequenceIds],
    });
  }

  loadNextPage(): void {
    if (
      this.snapshot.status !== "ready" ||
      !this.snapshot.hasNextPage ||
      this.snapshot.isLoadingNextPage
    ) {
      return;
    }
    void this.loadPage(this.page + 1, true, this.generation);
  }

  retry(): void {
    if (this.snapshot.status === "error") {
      this.resetAndLoad(this.snapshot.query);
    } else if (this.snapshot.nextPageError !== undefined) {
      void this.loadPage(this.page + 1, true, this.generation);
    }
  }

  getCachedSequence = (sequenceId: string): AnnotationSequenceSummary | null =>
    this.byId.get(sequenceId) ?? null;

  async ensureSequence(
    sequenceId: string,
    { signal }: { signal: AbortSignal },
  ): Promise<AnnotationSequenceSummary> {
    const cached = this.byId.get(sequenceId);
    if (cached !== undefined) return cached;
    const existing = this.ensureRequests.get(sequenceId);
    if (existing !== undefined) return awaitWithSignal(existing, signal);
    const promise = this.findSequence(sequenceId);
    this.ensureRequests.set(sequenceId, promise);
    try {
      return await awaitWithSignal(promise, signal);
    } finally {
      if (this.ensureRequests.get(sequenceId) === promise) this.ensureRequests.delete(sequenceId);
    }
  }

  refreshLocalFilters(): void {
    if (this.usesServerDirectory(this.snapshot.query)) return;
    this.publishLocal(this.snapshot.query);
  }

  // 언마운트 cleanup 이 호출한다. StrictMode 재마운트는 같은 인스턴스를 다시 쓴다. 그래서 진행 중
  // 요청만 끊고, start() 가 처음부터 다시 적재할 수 있는 idle 로 되돌린다. 구독의 수명은 조회
  // 수명과 다르다(재마운트 때 c6 controller 가 다시 구독한다). 그래서 구독은 비우지 않는다.
  dispose(): void {
    this.generation += 1;
    this.request?.abort();
    this.request = null;
    this.page = 0;
    this.publish({ ...this.snapshot, status: "idle", isLoadingNextPage: false });
  }

  private resetAndLoad(query: SequenceDirectoryQuery): void {
    this.generation += 1;
    this.request?.abort();
    this.request = null;
    this.page = 0;
    this.publish({
      query,
      items: [],
      totalSequenceCount: this.options.totalSequenceCount,
      streamFacets: [],
      hasNextPage: false,
      status: "loading",
      isLoadingNextPage: false,
    });
    if (this.usesServerDirectory(query)) {
      void this.loadPage(1, false, this.generation);
    } else {
      this.publishLocal(query);
    }
  }

  private usesServerDirectory(query: SequenceDirectoryQuery): boolean {
    return (query.contentScope ?? "all") === "all" && query.attentionSequenceIds === undefined;
  }

  private publishLocal(query: SequenceDirectoryQuery): void {
    const attention =
      query.attentionSequenceIds === undefined ? null : new Set(query.attentionSequenceIds);
    const contentScope = query.contentScope ?? "all";
    let items: readonly AnnotationSequenceSummary[];
    if (contentScope === "all") {
      items = [...(attention ?? [])]
        .map((id) => this.byId.get(id) ?? this.sequenceFromIdentity(id, query.streamId))
        .filter((item): item is AnnotationSequenceSummary => item !== null);
    } else {
      items = this.options.getContentSequences?.(contentScope, query.streamId) ?? [];
      if (attention !== null) items = items.filter((item) => attention.has(item.id));
    }
    const deduped = this.cacheSequences(items);
    this.publish({
      query,
      items: deduped,
      totalSequenceCount: this.options.totalSequenceCount,
      resultCount: deduped.length,
      streamFacets: [],
      hasNextPage: false,
      status: "ready",
      isLoadingNextPage: false,
    });
  }

  private async loadPage(page: number, append: boolean, generation: number): Promise<void> {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    if (append) {
      this.publish({
        ...this.snapshot,
        isLoadingNextPage: true,
        nextPageError: undefined,
      });
    }
    const camera = this.resolveCamera(this.snapshot.query.streamId);
    if (this.snapshot.query.streamId !== null && camera === null) {
      this.publishError(append);
      return;
    }
    try {
      const loadStructure = this.options.loadStructure ?? getAnnotationStructure;
      const response = await loadStructure(
        this.options.datasetUuid,
        { camera: camera ?? undefined, page, size: PAGE_SIZE, includeExcluded: false },
        { signal: request.signal },
      );
      if (request.signal.aborted || generation !== this.generation) return;
      this.options.onCameras?.(response.cameras);
      const streamId =
        response.camera === null ? null : this.options.identities.streamId(response.camera);
      const pageItems =
        streamId === null
          ? []
          : response.items.map((item) => {
              const sequence = {
                id: this.options.identities.sequenceId(item.name),
                label: item.name,
                streamIds: [streamId],
              } satisfies AnnotationSequenceSummary;
              return this.mergeCachedSequence(sequence);
            });
      const items = append
        ? this.cacheSequences([...this.snapshot.items, ...pageItems])
        : this.cacheSequences(pageItems);
      this.page = response.page;
      this.publish({
        query: this.snapshot.query,
        items,
        totalSequenceCount: this.options.totalSequenceCount,
        resultCount: response.total,
        streamFacets: streamId === null ? [] : [{ streamId, totalCount: response.total }],
        hasNextPage: response.page * response.size < response.total,
        status: "ready",
        isLoadingNextPage: false,
      });
    } catch {
      if (request.signal.aborted || generation !== this.generation) return;
      this.publishError(append);
    }
  }

  private publishError(append: boolean): void {
    this.publish(
      append
        ? {
            ...this.snapshot,
            status: "ready",
            isLoadingNextPage: false,
            nextPageError: DIRECTORY_ERROR,
          }
        : {
            ...this.snapshot,
            status: "error",
            isLoadingNextPage: false,
            error: DIRECTORY_ERROR,
          },
    );
  }

  private async findSequence(sequenceId: string): Promise<AnnotationSequenceSummary> {
    const episode = this.options.identities.episode(sequenceId);
    const camera = this.resolveCamera(this.snapshot.query.streamId);
    if (episode === null || camera === null)
      throw new Error("unknown annotation sequence identity");
    const loadStructure = this.options.loadStructure ?? getAnnotationStructure;
    const controller = new AbortController();
    let page = 1;
    while (true) {
      const response = await loadStructure(
        this.options.datasetUuid,
        { camera, page, size: PAGE_SIZE, includeExcluded: false },
        { signal: controller.signal },
      );
      this.options.onCameras?.(response.cameras);
      const streamId = this.options.identities.streamId(camera);
      for (const item of response.items) {
        const sequence = this.mergeCachedSequence({
          id: this.options.identities.sequenceId(item.name),
          label: item.name,
          streamIds: [streamId],
        });
        if (sequence.id === sequenceId) return sequence;
      }
      if (response.page * response.size >= response.total) break;
      page += 1;
    }
    throw new Error("annotation sequence not found");
  }

  private sequenceFromIdentity(
    sequenceId: string,
    streamId: string | null,
  ): AnnotationSequenceSummary | null {
    const episode = this.options.identities.episode(sequenceId);
    if (episode === null || streamId === null) return null;
    return this.mergeCachedSequence({ id: sequenceId, label: episode, streamIds: [streamId] });
  }

  private resolveCamera(streamId: string | null): string | null {
    return streamId === null ? null : this.options.identities.camera(streamId);
  }

  private cacheSequences(
    sequences: readonly AnnotationSequenceSummary[],
  ): AnnotationSequenceSummary[] {
    const unique = new Map<string, AnnotationSequenceSummary>();
    for (const sequence of sequences) unique.set(sequence.id, this.mergeCachedSequence(sequence));
    return [...unique.values()];
  }

  private mergeCachedSequence(sequence: AnnotationSequenceSummary): AnnotationSequenceSummary {
    const existing = this.byId.get(sequence.id);
    const merged =
      existing === undefined
        ? sequence
        : {
            ...existing,
            ...sequence,
            streamIds: [...new Set([...existing.streamIds, ...sequence.streamIds])],
          };
    this.byId.set(sequence.id, merged);
    return merged;
  }

  private publish(snapshot: AnnotationSequenceDirectorySnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }
}

function awaitWithSignal<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}
